/**
 * Daten-Import mit Merge-Review — the React port of app/web/import.html.
 *
 * The flow of the original page is kept exactly, because it is the mental model
 * the users already have: upload a CSV → the server DIFFS it against the CMDBuild
 * stock and writes nothing → every changed entry can individually be taken over
 * ("Übernehmen") or left alone ("Behalten") → apply.
 *
 * What the port had to get right beyond the markup:
 *   - The decision map is transmitted IN FULL for every non-'unchanged' entry. A
 *     missing entry_id defaults to 'incoming' server-side, so a partial map would
 *     silently import what the user rejected.
 *   - Plans live in an in-process store with a 30-minute TTL, so `apply` can
 *     answer 404 ("Plan nicht (mehr) vorhanden — bitte CSV erneut hochladen…").
 *     That message is shown verbatim and paired with a one-click path back: the
 *     picked File is kept in state, so the same file can simply be re-compared.
 *     The page also warns BEFORE the user tries, once the TTL has elapsed.
 *   - Unknown classes cannot vanish: known classes render in schema order, then
 *     anything else the server sent, under its raw name (`ImportKlassLoose`).
 *   - Import is a write action. The route already requires `import.write`, but the
 *     component checks it again and disables every control — an auditor sees the
 *     explanation instead of a dead button.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Banner } from '../components/Banner';
import { Chip, ChipList } from '../components/Chip';
import { Icon } from '../components/Icon';
import { KpiRow } from '../components/KpiTile';
import PageHeader from '../components/PageHeader';
import { PermissionDenied } from '../components/RequirePermission';
import Spinner from '../components/Spinner';
import { useToast } from '../components/Toast';
import { ApiError, applyImport, errorMessage, fetchCmdbCard, planImport } from '../lib/api';
import { useAuth } from '../lib/auth';
import { countLabel, uniqueStrings } from '../lib/format';
import { IMPORT_KLASS_LABELS, IMPORT_KLASS_ORDER } from '../lib/types';
import type {
  ImportApplyResult,
  ImportDecision,
  ImportPlan,
  ImportTargetRequest,
  InstanceVersionChoice,
  KpiCounter,
  PlanChange,
  PlanEntry,
} from '../lib/types';

import './admin/ops.css';
import { BomReview } from './import/BomReview';
import { ImportTargetPanel, type ProductTargetState } from './import/ImportTargetPanel';
import { TargetSummary } from './import/TargetSummary';
import { PlanDecision } from './import/PlanDecision';
import { buildPlanPresentation, type EntryPresentation } from './import/planPresentation';
import './import/import-review.css';

/** Server-side plan TTL (app/services/import_service.py). */
const PLAN_TTL_MS = 30 * 60 * 1000;

/** How often the "plan expires at" hint is re-evaluated. */
const TICK_MS = 15_000;

function klassLabel(klass: string): string {
  const labels: Readonly<Record<string, string | undefined>> = IMPORT_KLASS_LABELS;
  return labels[klass] ?? klass;
}

const ACTION_SYMBOL: Readonly<Record<string, string>> = {
  create: '+',
  update: '~',
  unchanged: '=',
  remove: '−',
};

const ACTION_CLASS: Readonly<Record<string, string>> = {
  create: 'add',
  update: 'mod',
  unchanged: 'same',
  remove: 'del',
};

/**
 * Starting decisions: take the file for creates/updates (the server default),
 * KEEP components the scan lacks, and leave ambiguous matches undecided — the
 * server refuses the apply until the operator picked one. Catalog entries that
 * follow the Stückliste carry no decision of their own.
 */
function initialDecisions(plan: ImportPlan): Record<string, ImportDecision> {
  const undecided = new Set(plan.requires_decision ?? []);
  const next: Record<string, ImportDecision> = {};
  for (const entry of plan.entries) {
    if (entry.action === 'unchanged' || undecided.has(entry.entry_id)) continue;
    if ((entry.display?.follows?.length ?? 0) > 0) continue;
    next[entry.entry_id] = entry.action === 'remove' ? 'existing' : 'incoming';
  }
  return next;
}

function isChange(decision: ImportDecision): boolean {
  return decision === 'incoming' || decision.startsWith('match:');
}

/** One field-level diff line — old struck through, new highlighted. */
function DiffLine({ change }: { change: PlanChange }): ReactNode {
  return (
    <div className="diff">
      <span className="f">{change.field}:</span>{' '}
      {change.old === null ? (
        <span className="new">{change.new}</span>
      ) : (
        <>
          <span className="visually-hidden">Bisher: </span>
          <del className="old">{change.old}</del>
          <Icon name="arrow-right" size={12} className="arr" />
          <span className="visually-hidden">Neu: </span>
          <span className="new">{change.new}</span>
        </>
      )}
    </div>
  );
}

interface PlanEntryItemProps {
  entry: PlanEntry;
  presentation: EntryPresentation | undefined;
  /** null for 'unchanged' entries, which carry no decision. */
  decision: ImportDecision | null;
  /** All decisions — entries that follow the Stückliste derive their state from them. */
  decisions: Readonly<Record<string, ImportDecision>>;
  disabled: boolean;
  onDecide: (entryId: string, decision: ImportDecision) => void;
}

/** Explains a catalog entry that is created together with its Stückliste entry. */
function FollowsNote({ entry, created }: { entry: PlanEntry; created: boolean }): ReactNode {
  const type = entry.klass === 'KomponenteType';
  return (
    <p className="imp-follows-note">
      {type ? 'Neue Komponente aus dem Scan. Sie' : 'Neue Komponentenversion aus dem Scan. Sie'} wird im
      Komponentenkatalog nur angelegt, wenn Sie {type ? 'eine ihrer Versionen' : 'sie'} unten unter
      »Stücklisten« übernehmen.
      {created ? null : type ? ' Dort sind derzeit alle ihre Versionen übersprungen.' : ' Dort ist sie derzeit übersprungen.'}
    </p>
  );
}

function PlanEntryItem({ entry, presentation, decision, decisions, disabled, onDecide }: PlanEntryItemProps): ReactNode {
  const follows = entry.display?.follows ?? [];
  const followsCreated = follows.some((id) => decisions[id] === 'incoming');
  const classes = ['entry', 'imp-entry', ACTION_CLASS[entry.action] ?? 'same'];
  if (decision === 'existing' || (follows.length > 0 && !followsCreated)) classes.push('skipped');
  const title = presentation?.title ?? entry.key;
  const changes = presentation?.changes ?? entry.changes;

  return (
    <div className={classes.join(' ')}>
      <div className="sym" aria-hidden="true">
        {ACTION_SYMBOL[entry.action] ?? '?'}
      </div>
      <div className="body">
        <div className="imp-entry-title">
          <span className="key">{title}</span>
          {presentation?.version ? <Chip className="imp-version">Version {presentation.version}</Chip> : null}
        </div>
        {entry.action === 'unchanged' ? null : (
          <div className="diffs">
            {changes.length === 0 ? (
              <div className="diff">
                <span className="f">keine Feldänderungen gemeldet</span>
              </div>
            ) : (
              changes.map((change, index) => (
                <DiffLine key={`${change.field}-${index}`} change={change} />
              ))
            )}
          </div>
        )}
        {follows.length > 0 ? <FollowsNote entry={entry} created={followsCreated} /> : null}
      </div>
      {follows.length > 0 ? (
        <Chip className="imp-follows" tone={followsCreated ? 'good' : 'neutral'}>
          {followsCreated ? 'Wird mit der Stückliste angelegt' : 'Wird nicht angelegt'}
        </Chip>
      ) : (
        <PlanDecision
          entry={entry}
          label={`${title}${presentation?.version ? ` · Version ${presentation.version}` : ''}`}
          decision={decision}
          disabled={disabled}
          onDecide={onDecide}
        />
      )}
    </div>
  );
}

export function ImportPage(): ReactNode {
  const { has } = useAuth();
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [searchParams] = useSearchParams();

  const canImport = has('import.write');

  // `/import?target=product&product=<id>` — SBOM upload onto one product (DEV-107).
  const targetProductId = useMemo<number | null>(() => {
    if (searchParams.get('target') !== 'product') return null;
    const id = Number(searchParams.get('product'));
    return Number.isInteger(id) && id > 0 ? id : null;
  }, [searchParams]);
  // `&version=<id>` — opened from a version row: preselect that version.
  const targetVersionId = useMemo<number | null>(() => {
    const id = Number(searchParams.get('version'));
    return targetProductId !== null && Number.isInteger(id) && id > 0 ? id : null;
  }, [searchParams, targetProductId]);
  const [productTarget, setProductTarget] = useState<ProductTargetState>({
    productName: null, request: null,
  });
  // `/import?target=instance&instance=<id>` — one delivered instance's scan.
  const targetInstanceId = useMemo<number | null>(() => {
    if (searchParams.get('target') !== 'instance') return null;
    const id = Number(searchParams.get('instance'));
    return Number.isInteger(id) && id > 0 ? id : null;
  }, [searchParams]);
  const [instanceLabel, setInstanceLabel] = useState<string | null>(null);
  useEffect(() => {
    if (targetInstanceId === null) return undefined;
    let cancelled = false;
    fetchCmdbCard('ProduktInstanz', targetInstanceId)
      .then((row) => { if (!cancelled) setInstanceLabel(row.label); })
      .catch(() => { if (!cancelled) setInstanceLabel(null); });
    return () => { cancelled = true; };
  }, [targetInstanceId]);
  const targetRequest = useMemo<ImportTargetRequest | null>(
    () => (targetInstanceId !== null
      ? { target: 'instance', instance_id: targetInstanceId }
      : productTarget.request),
    [targetInstanceId, productTarget.request],
  );
  const hasTarget = targetProductId !== null || targetInstanceId !== null;
  const [versionChoice, setVersionChoice] = useState<InstanceVersionChoice | null>(null);

  const [file, setFile] = useState<File | null>(null);
  const [filename, setFilename] = useState<string | null>(null);
  const [plan, setPlan] = useState<ImportPlan | null>(null);
  const [plannedAt, setPlannedAt] = useState<number | null>(null);
  const [decisions, setDecisions] = useState<Readonly<Record<string, ImportDecision>>>({});
  const [showUnchanged, setShowUnchanged] = useState(false);
  const [planning, setPlanning] = useState(false);
  const [planError, setPlanError] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [planGone, setPlanGone] = useState(false);
  const [result, setResult] = useState<ImportApplyResult | null>(null);
  const [dragging, setDragging] = useState(false);
  const [now, setNow] = useState<number>(() => Date.now());

  // Keeps the "Plan gültig bis …" hint honest without a per-second timer.
  useEffect(() => {
    if (plannedAt === null) return undefined;
    const timer = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(timer);
  }, [plannedAt]);

  const runPlan = useCallback(
    async (candidate: File): Promise<void> => {
      setFile(candidate);
      setPlanning(true);
      setPlanError(null);
      setApplyError(null);
      setPlanGone(false);
      setResult(null);
      try {
        const response = await planImport(candidate, targetRequest ?? undefined);
        setPlan(response.plan);
        setFilename(response.filename ?? candidate.name);
        setPlannedAt(Date.now());
        setNow(Date.now());
        setDecisions(initialDecisions(response.plan));
      } catch (err) {
        setPlan(null);
        setPlannedAt(null);
        setDecisions({});
        setPlanError(errorMessage(err));
      } finally {
        setPlanning(false);
      }
    },
    [targetRequest],
  );

  const targetIncomplete = hasTarget && targetRequest === null;

  const onPick = useCallback(
    (candidate: File | undefined): void => {
      if (candidate === undefined || !canImport || targetIncomplete) return;
      void runPlan(candidate);
    },
    [canImport, runPlan, targetIncomplete],
  );

  const setDecision = useCallback((entryId: string, decision: ImportDecision): void => {
    setDecisions((current) => ({ ...current, [entryId]: decision }));
  }, []);

  // "Alle übernehmen/überspringen" covers creates and updates only: removing
  // every component a scan missed, or guessing ambiguous matches, must stay
  // an individual decision.
  const setAll = useCallback((decision: ImportDecision): void => {
    if (plan === null) return;
    const bulk = new Set(
      plan.entries
        .filter((entry) => entry.action === 'create' || entry.action === 'update')
        .filter((entry) => !(plan.requires_decision ?? []).includes(entry.entry_id))
        .map((entry) => entry.entry_id),
    );
    setDecisions((current) => {
      const next: Record<string, ImportDecision> = { ...current };
      for (const key of Object.keys(current)) if (bulk.has(key)) next[key] = decision;
      return next;
    });
  }, [plan]);

  const reset = useCallback((): void => {
    setFile(null);
    setFilename(null);
    setPlan(null);
    setPlannedAt(null);
    setDecisions({});
    setPlanError(null);
    setApplyError(null);
    setPlanGone(false);
    setResult(null);
    setVersionChoice(null);
    if (inputRef.current !== null) inputRef.current.value = '';
  }, []);

  const decisionValues = useMemo<readonly ImportDecision[]>(
    () => Object.values(decisions),
    [decisions],
  );
  const incomingCount = decisionValues.filter(isChange).length;
  const keepCount = decisionValues.length - incomingCount;
  const undecidedCount = (plan?.requires_decision ?? []).filter((id) => decisions[id] === undefined).length;
  // An instance upload can switch the instance's version without a single
  // catalog change; a deviation needs the custom/official choice first.
  const instanceTarget = plan?.target?.kind === 'instance' ? plan.target : null;
  const needsChoice = instanceTarget?.outcome === 'deviation' && versionChoice === null;
  const nothingToApply = instanceTarget !== null
    ? instanceTarget.outcome === 'identical' && incomingCount === 0
    : incomingCount === 0;

  const expiresAt = plannedAt === null ? null : plannedAt + PLAN_TTL_MS;
  const expired = expiresAt !== null && now >= expiresAt;

  const apply = useCallback(async (): Promise<void> => {
    if (plan === null || !canImport) return;
    setApplying(true);
    setApplyError(null);
    setPlanGone(false);
    try {
      // The FULL map for every decidable entry — see the module docstring.
      const response = await applyImport({
        plan_id: plan.plan_id,
        decisions: { ...decisions },
        ...(versionChoice !== null ? { version_choice: versionChoice } : {}),
      });
      setResult(response.result);
      setPlan(null);
      setPlannedAt(null);
      const removed = response.result.removed?.length ?? 0;
      const instanceResult = response.result.instance;
      if (instanceResult?.action === 'switched' || instanceResult?.action === 'created') {
        toast.success(
          instanceResult.action === 'switched'
            ? `Produktinstanz auf Version ${instanceResult.version_nr ?? ''} umgestellt.`
            : `${instanceResult.custom ? 'Kundenversion' : 'Version'} ${instanceResult.version_nr ?? ''} angelegt und zugewiesen.`,
        );
      }
      if (response.result.errors.length > 0) {
        toast.error(
          `Import mit ${countLabel(response.result.errors.length, 'Fehler', 'Fehlern')} abgeschlossen.`,
        );
      } else {
        toast.success(
          `Import angewendet: ${response.result.created.length} angelegt, ${response.result.updated.length} aktualisiert`
          + (removed > 0 ? `, ${removed} entfernt.` : '.'),
        );
      }
    } catch (err) {
      setApplyError(errorMessage(err));
      // 404 = the in-process plan expired; offer the way back to the upload.
      if (err instanceof ApiError && err.status === 404) setPlanGone(true);
    } finally {
      setApplying(false);
    }
  }, [plan, canImport, decisions, toast, versionChoice]);

  /** Known classes in schema order, then anything else the server sent. */
  const groups = useMemo(() => {
    if (plan === null) return [];
    const known: readonly string[] = IMPORT_KLASS_ORDER;
    const extra = uniqueStrings(plan.entries.map((entry) => entry.klass))
      .filter((klass) => !known.includes(klass))
      .sort((a, b) => a.localeCompare(b, 'de-DE'));
    return [...known, ...extra]
      .map((klass) => ({
        klass,
        label: klassLabel(klass),
        entries: plan.entries.filter(
          (entry) =>
            entry.klass === klass && (showUnchanged || entry.action !== 'unchanged'),
        ),
      }))
      .filter((group) => group.entries.length > 0);
  }, [plan, showUnchanged]);

  const presentation = useMemo(() => buildPlanPresentation(plan?.entries ?? []), [plan]);

  const untouchedTotal = useMemo<number>(() => {
    if (plan === null) return 0;
    return Object.values(plan.untouched_in_cmdb).reduce((sum, count) => sum + count, 0);
  }, [plan]);

  const counters = useMemo<readonly KpiCounter[]>(() => {
    if (plan === null) return [];
    return [
      { label: 'Einträge aus Datei', value: plan.total_rows, tone: 'neutral' },
      { label: 'Neu (+)', value: plan.creates, tone: plan.creates > 0 ? 'good' : 'neutral' },
      { label: 'Geändert (~)', value: plan.updates, tone: plan.updates > 0 ? 'warn' : 'neutral' },
      { label: 'Unverändert (=)', value: plan.unchanged, tone: 'neutral' },
      ...((plan.target?.kind === 'product' && plan.target.mode === 'update') || plan.target?.kind === 'instance'
        ? [{
            label: 'Fehlt im Scan (−)',
            value: plan.removes ?? 0,
            tone: (plan.removes ?? 0) > 0 ? 'warn' : 'neutral',
            title: 'Komponenten der Stückliste, die die SBOM nicht enthält. Sie bleiben, sofern Sie nicht „Entfernen“ wählen.',
          } satisfies KpiCounter]
        : []),
      {
        label: 'Nur in CMDB (bleibt)',
        value: untouchedTotal,
        tone: 'neutral',
        title: 'Einträge im CMDB-Bestand, die in der Datei fehlen. Ein Import löscht nie.',
      },
    ];
  }, [plan, untouchedTotal]);

  // Defence in depth: the route is already guarded, but a stale bundle whose
  // route table lost the guard must not show an armed upload. Reuses the shared
  // panel so there is one wording of the refusal in the whole SPA.
  if (!canImport) return <PermissionDenied permission="import.write" />;

  // While a finished import is on screen the picker is replaced by the explicit
  // "Weitere Datei importieren" button, so the result cannot be mistaken for a
  // preview of the next file.
  const showDropZone = plan === null && result === null;

  return (
    <>
      <PageHeader
        title={
          targetProductId !== null
            ? `SBOM für ${productTarget.productName ?? 'Produkt'} hochladen`
            : targetInstanceId !== null
              ? `Scan der Produktinstanz ${instanceLabel ?? ''} hochladen`.trim()
              : 'Daten-Import mit Merge-Review'
        }
        sub={
          plan !== null ? (
            <>
              {filename !== null ? <span className="imp-file">{filename}</span> : null}
              {filename !== null && expiresAt !== null ? ' · ' : null}
              {expiresAt !== null
                ? `Plan gültig bis ${new Date(expiresAt).toLocaleTimeString('de-DE')}`
                : null}
            </>
          ) : undefined
        }
        actions={
          plan !== null ? (
            <button type="button" className="ghost" onClick={reset} disabled={applying}>
              Abbrechen / neue Datei
            </button>
          ) : null
        }
      />

      {targetProductId !== null && result === null ? (
        <ImportTargetPanel
          key={`${targetProductId}:${targetVersionId ?? ''}`}
          productId={targetProductId}
          initialVersionId={targetVersionId}
          disabled={planning || applying || plan !== null}
          onChange={setProductTarget}
        />
      ) : null}

      {showDropZone ? (
        <>
          <button
            type="button"
            className={dragging ? 'drop hover' : 'drop'}
            onClick={() => inputRef.current?.click()}
            disabled={planning || targetIncomplete}
            onDragOver={(event) => {
              event.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(event) => {
              event.preventDefault();
              setDragging(false);
              onPick(event.dataTransfer.files[0]);
            }}
          >
            {/* Spans, not divs: a <button> may only contain phrasing content. */}
            <span className="imp-drop-line">
              {hasTarget ? (
                <><b>CycloneDX-SBOM (JSON) hierher ziehen</b> oder klicken zum Auswählen</>
              ) : (
                <><b>AssetRadar CSV oder CycloneDX-JSON hierher ziehen</b> oder klicken zum Auswählen</>
              )}
            </span>
            <span className="imp-drop-line imp-drop-hint">
              {targetIncomplete
                ? 'Wählen Sie zuerst das Ziel der SBOM.'
                : 'Vor dem Schreiben sehen Sie den Vergleich mit dem CMDB-Bestand.'}
            </span>
          </button>
          <input
            ref={inputRef}
            type="file"
            accept={
              hasTarget
                ? '.json,application/json,application/vnd.cyclonedx+json'
                : '.csv,.json,text/csv,application/json,application/vnd.cyclonedx+json'
            }
            hidden
            onChange={(event) => {
              const picked = event.target.files?.[0];
              // Reset so choosing the same file again fires 'change' again.
              event.target.value = '';
              onPick(picked);
            }}
          />
        </>
      ) : null}

      {planning ? (
        <Banner kind="info">
          <Spinner />{' '}
          {file !== null
            ? `Vergleiche »${file.name}« mit dem CMDB-Bestand…`
            : 'Vergleiche mit dem CMDB-Bestand…'}
        </Banner>
      ) : null}

      {planError !== null ? (
        <Banner
          kind="error"
          sub="Es wurde nichts geschrieben; laden Sie die korrigierte Datei erneut hoch."
        >
          Plan fehlgeschlagen: {planError}
        </Banner>
      ) : null}

      {plan !== null ? (
        <>
          {plan.target !== undefined ? (
            <TargetSummary
              key={plan.plan_id}
              target={plan.target}
              disabled={applying}
              onChoice={setVersionChoice}
            />
          ) : null}
          {undecidedCount > 0 ? (
            <Banner kind="warn" sub="Wählen Sie den passenden Katalogeintrag oder legen Sie die Komponente neu an.">
              {countLabel(undecidedCount, 'Komponente ist', 'Komponenten sind')} nicht eindeutig zuzuordnen.
            </Banner>
          ) : null}
          {plan.errors.length > 0 ? (
            <Banner
              kind="warn"
              items={plan.errors}
              sub="Prüfen Sie die Hinweise vor dem Übernehmen."
            >
              {countLabel(plan.errors.length, 'Hinweis', 'Hinweise')} zum Import.
            </Banner>
          ) : null}

          {expired ? (
            <Banner
              kind="warn"
              sub="Pläne verfallen serverseitig nach 30 Minuten."
              items={file !== null ? undefined : ['Wählen Sie die Datei erneut aus.']}
            >
              Dieser Plan ist wahrscheinlich verfallen.{' '}
              {file !== null ? (
                <button
                  type="button"
                  className="link"
                  onClick={() => void runPlan(file)}
                  disabled={planning}
                >
                  »{file.name}« erneut vergleichen
                </button>
              ) : null}
            </Banner>
          ) : null}

          <KpiRow counters={counters} />

          {untouchedTotal > 0 ? (
            <div className="imp-untouched">
              <span className="imp-untouched-label">
                Nur im CMDB vorhanden (bleibt unangetastet):
              </span>
              <ChipList>
                {Object.entries(plan.untouched_in_cmdb)
                  .filter(([, count]) => count > 0)
                  .map(([klass, count]) => (
                    <Chip key={klass}>
                      {klassLabel(klass)}: {count}
                    </Chip>
                  ))}
              </ChipList>
            </div>
          ) : null}

          <div className="controls">
            <button
              type="button"
              onClick={() => setAll('incoming')}
              disabled={applying || decisionValues.length === 0}
            >
              Alle übernehmen
            </button>
            <button
              type="button"
              onClick={() => setAll('existing')}
              disabled={applying || decisionValues.length === 0}
            >
              Alle überspringen
            </button>
            <label className="check">
              <input
                type="checkbox"
                checked={showUnchanged}
                onChange={(event) => setShowUnchanged(event.target.checked)}
              />
              Unveränderte anzeigen
            </label>
            <div className="spacer" />
            <span className="muted">{filename ?? ''}</span>
          </div>

          {groups.length === 0 ? (
            <Banner kind="ok">
              Keine Unterschiede: Der Dateiinhalt entspricht dem CMDB-Bestand.
            </Banner>
          ) : (
            groups.map((group) => (
              <section className="klass-block" key={group.klass}>
                <h2>{group.label}</h2>
                {group.klass === 'ProduktVersionKomponente' ? (
                  <BomReview
                    groups={presentation.bomGroups}
                    decisions={decisions}
                    showUnchanged={showUnchanged}
                    disabled={applying}
                    onDecide={setDecision}
                  />
                ) : group.entries.map((entry) => (
                  <PlanEntryItem
                    key={entry.entry_id}
                    entry={entry}
                    presentation={presentation.byEntryId.get(entry.entry_id)}
                    decision={decisions[entry.entry_id] ?? null}
                    decisions={decisions}
                    disabled={applying}
                    onDecide={setDecision}
                  />
                ))}
              </section>
            ))
          )}

          {applyError !== null ? (
            <Banner kind="error">
              Anwenden fehlgeschlagen: {applyError}
              {planGone && file !== null ? (
                <div className="imp-retry">
                  <button type="button" className="primary small" onClick={() => void runPlan(file)}>
                    »{file.name}« erneut hochladen und vergleichen
                  </button>
                </div>
              ) : null}
              {planGone && file === null ? (
                <div className="imp-retry">
                  <button type="button" className="primary small" onClick={reset}>
                    Neue Datei auswählen
                  </button>
                </div>
              ) : null}
            </Banner>
          ) : null}

          <div className="applybar">
            <button
              type="button"
              className="primary"
              onClick={() => void apply()}
              disabled={applying || nothingToApply || undecidedCount > 0 || needsChoice}
              title={
                undecidedCount > 0
                  ? 'Entscheiden Sie zuerst über die nicht eindeutig zugeordneten Komponenten.'
                  : needsChoice
                    ? 'Wählen Sie, welche Version für die abweichende Konfiguration angelegt wird.'
                    : nothingToApply
                      ? 'Keine Änderung ausgewählt. Mindestens ein Eintrag muss übernommen werden.'
                      : undefined
              }
            >
              {applying ? (
                <Spinner variant="on-accent" label="Wende an…" />
              ) : instanceTarget !== null ? (
                'Übernehmen'
              ) : (
                `Import anwenden (${countLabel(incomingCount, 'Änderung', 'Änderungen')})`
              )}
            </button>
            <span className="muted">
              {applying
                ? 'Wende an…'
                : keepCount > 0
                  ? `${keepCount} werden behalten/übersprungen`
                  : ''}
            </span>
            <div className="spacer" />
            <button type="button" className="ghost" onClick={reset} disabled={applying}>
              Abbrechen / neue Datei
            </button>
          </div>
        </>
      ) : null}

      {result !== null ? (
        <>
          <Banner kind={result.errors.length > 0 ? 'error' : 'ok'}>
            Import abgeschlossen: <b>{result.created.length}</b> angelegt,{' '}
            <b>{result.updated.length}</b> aktualisiert,{' '}
            {(result.removed?.length ?? 0) > 0 ? <><b>{result.removed?.length}</b> entfernt, </> : null}
            {result.skipped.length} behalten/übersprungen
            {result.errors.length > 0 ? (
              <>
                , <b>{countLabel(result.errors.length, 'Fehler', 'Fehler')}</b>
              </>
            ) : null}
            .
          </Banner>
          {result.instance !== undefined && result.instance.action !== 'failed' ? (
            <Banner kind="ok">
              {result.instance.action === 'unchanged'
                ? <>Die Produktinstanz bleibt auf Version <b>{result.instance.version_nr}</b>.</>
                : result.instance.action === 'switched'
                  ? <>Die Produktinstanz läuft jetzt auf Version <b>{result.instance.version_nr}</b>.</>
                  : <>{result.instance.custom ? 'Kundenversion' : 'Offizielle Version'} <b>{result.instance.version_nr}</b> wurde angelegt und der Produktinstanz zugewiesen.</>}
            </Banner>
          ) : null}
          {result.errors.length > 0 ? (
            <Banner kind="error" items={result.errors}>
              Diese Einträge wurden nicht geschrieben:
            </Banner>
          ) : null}
          <Banner kind="info">
            Die Änderungen werden an Dependency-Track übertragen; das Dashboard aktualisiert sich nach
            der Analyse.
          </Banner>
          <div className="row">
            <button type="button" className="primary" onClick={reset}>
              Weitere Datei importieren
            </button>
            {targetProductId !== null ? (
              <Link className="ghost" to="/stammdaten?klasse=ProduktVersion">Zu den Produktversionen</Link>
            ) : null}
            {targetInstanceId !== null ? (
              <Link className="ghost" to={`/stammdaten?klasse=ProduktInstanz&id=${targetInstanceId}`}>
                Zur Produktinstanz
              </Link>
            ) : null}
          </div>
        </>
      ) : null}
    </>
  );
}

export default ImportPage;
